import { Box, Typography, Container, Stack, Chip } from '@mui/material';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { Business, Public, Storage, Accessibility, Stars, Apartment, Hotel, Language } from '@mui/icons-material';

const Timeline = () => {
  const targetRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ['start end', 'end start'],
  });

  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.3, 1, 0.3]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1, 0.8]);

  const milestones = [
    { 
      year: '1990', 
      title: 'Founded Skyline Estates', 
      description: 'John engineered over three decades of strategic acquisition, Renovate /Reposition, and management of 76 Commercial Real Estate buildings including Apartment Rental Buildings, Commercial Rental Buildings, and a North American Hotel & Resort chain.',
      icon: <Business />,
      color: '#C8A96B',
      highlight: true
    },
    { 
      year: '2008', 
      title: 'International Expansion & Diversification', 
      description: 'During the Great Recession caused by Banking Derivative Fraud and the Credit Crisis of 2007/2008, the decision was made to expand and diversify internationally. Building a worldwide business presence by investing specifically in "essential service" Assets worldwide, while shifting away from "disposable income" dependent business/services including Tourism, Hotels & Resorts.',
      icon: <Public />,
      color: '#C8A96B',
      highlight: true
    },
    { 
      year: '2012', 
      title: 'Global Infrastructure', 
      description: 'Established worldwide logistics and communication networks across 30+ countries, focusing on essential service assets and sustainable investments.',
      icon: <Storage />,
      color: '#C8A96B'
    },
    { 
      year: '2018', 
      title: 'Direct Access Initiative', 
      description: 'Launched the "No Gatekeepers" platform, revolutionizing executive accessibility and transparent communication.',
      icon: <Accessibility />,
      color: '#C8A96B'
    },
    { 
      year: '2024', 
      title: 'Global Leadership', 
      description: 'Recognized as a pioneer in executive accessibility, strategic diversification, and transparent communication across worldwide operations.',
      icon: <Stars />,
      color: '#C8A96B'
    },
  ];

  return (
    <Box 
      ref={targetRef} 
      sx={{ 
        py: { xs: 10, md: 18 }, 
        background: 'linear-gradient(180deg, #0a0a0a 0%, #0f0f0f 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="lg">
        <motion.div style={{ opacity, scale }}>
          <Typography
            variant="subtitle1"
            sx={{ 
              color: '#C8A96B', 
              letterSpacing: 6, 
              mb: 2, 
              textAlign: 'center',
              fontSize: '0.85rem',
              fontWeight: 600,
            }}
          >
            CAREER MILESTONES
          </Typography>

          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: 2,
              fontSize: { xs: '2.2rem', sm: '3rem', md: '3.8rem' },
              fontWeight: 800,
              background: 'linear-gradient(135deg, #fff 0%, #e0e0e0 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            The Journey
          </Typography>

          <Typography
            sx={{
              textAlign: 'center',
              maxWidth: '700px',
              mx: 'auto',
              color: '#aaa',
              mb: 10,
              fontSize: '1rem',
              lineHeight: 1.6,
            }}
          >
            From commercial real estate mogul to global investment strategist — adapting and thriving through economic transformation
          </Typography>
        </motion.div>

        <Stack spacing={5} sx={{ position: 'relative' }}>
          {/* Vertical Timeline Line - Desktop Only */}
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '2px',
              height: '90%',
              background: 'linear-gradient(to bottom, #C8A96B, rgba(200,169,107,0.2), #C8A96B)',
              display: { xs: 'none', md: 'block' },
            }}
          />

          {milestones.map((milestone, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -60 : 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: index * 0.15, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  justifyContent: index % 2 === 0 ? 'flex-start' : 'flex-end',
                  alignItems: 'center',
                  gap: 3,
                  position: 'relative',
                }}
              >
                {/* Timeline Node */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: { md: '50%' },
                    transform: { md: 'translateX(-50%)' },
                    width: { xs: '100%', md: 'auto' },
                    display: 'flex',
                    justifyContent: 'center',
                    zIndex: 2,
                  }}
                >
                  <motion.div
                    whileHover={{ scale: 1.2, rotate: 360 }}
                    transition={{ duration: 0.4 }}
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #111, #1a1a1a)',
                      border: `3px solid ${milestone.color}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: milestone.color,
                      boxShadow: '0 0 30px rgba(200,169,107,0.4)',
                    }}
                  >
                    {milestone.icon}
                  </motion.div>
                </Box>

                {/* Content Box */}
                <Box
                  sx={{
                    width: { xs: '100%', md: '45%' },
                    ml: { md: index % 2 === 0 ? 0 : 'auto' },
                    mr: { md: index % 2 === 0 ? 'auto' : 0 },
                    background: milestone.highlight 
                      ? 'linear-gradient(135deg, #111 0%, #1a1610 100%)'
                      : 'linear-gradient(135deg, #111 0%, #161616 100%)',
                    borderRadius: '20px',
                    p: 3.5,
                    border: milestone.highlight 
                      ? '1px solid rgba(200,169,107,0.3)'
                      : '1px solid rgba(200,169,107,0.15)',
                    transition: 'all 0.3s',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': milestone.highlight ? {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '2px',
                      background: 'linear-gradient(90deg, transparent, #C8A96B, transparent)',
                    } : {},
                    '&:hover': {
                      borderColor: '#C8A96B',
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                    },
                  }}
                >
                  <Chip
                    label={milestone.year}
                    size="small"
                    sx={{
                      background: 'rgba(200,169,107,0.15)',
                      color: '#C8A96B',
                      fontWeight: 700,
                      mb: 2,
                      letterSpacing: 1,
                      fontSize: '0.85rem',
                    }}
                  />
                  <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 700, color: '#fff' }}>
                    {milestone.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#bbb', lineHeight: 1.7, fontSize: '0.95rem' }}>
                    {milestone.description}
                  </Typography>
                  
                  {/* Key Stats for 1990 milestone */}
                  {milestone.year === '1990' && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(200,169,107,0.1)' }}>
                      <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ gap: 1 }}>
                        <Chip 
                          label="76 Properties" 
                          size="small" 
                          sx={{ background: 'rgba(200,169,107,0.1)', color: '#C8A96B' }} 
                        />
                        <Chip 
                          label="Commercial Portfolio" 
                          size="small" 
                          sx={{ background: 'rgba(200,169,107,0.1)', color: '#C8A96B' }} 
                        />
                        <Chip 
                          label="Hotel & Resort Chain" 
                          size="small" 
                          sx={{ background: 'rgba(200,169,107,0.1)', color: '#C8A96B' }} 
                        />
                      </Stack>
                    </Box>
                  )}
                  
                  {/* Key Stats for 2008 milestone */}
                  {milestone.year === '2008' && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(200,169,107,0.1)' }}>
                      <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ gap: 1 }}>
                        <Chip 
                          label="Essential Services Focus" 
                          size="small" 
                          sx={{ background: 'rgba(200,169,107,0.1)', color: '#C8A96B' }} 
                        />
                        <Chip 
                          label="Global Diversification" 
                          size="small" 
                          sx={{ background: 'rgba(200,169,107,0.1)', color: '#C8A96B' }} 
                        />
                        <Chip 
                          label="Strategic Pivot" 
                          size="small" 
                          sx={{ background: 'rgba(200,169,107,0.1)', color: '#C8A96B' }} 
                        />
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Box>
            </motion.div>
          ))}
        </Stack>

        {/* Bottom Quote Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <Box
            sx={{
              mt: 10,
              pt: 5,
              textAlign: 'center',
              borderTop: '1px solid rgba(200,169,107,0.1)',
            }}
          >
            <Typography
              sx={{
                color: '#C8A96B',
                fontStyle: 'italic',
                fontSize: '1.1rem',
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              "Crisis creates opportunity. By shifting from disposable income dependency to essential services,
              we built resilience through strategic diversification."
            </Typography>
            <Typography
              sx={{
                color: '#777',
                mt: 2,
                fontSize: '0.9rem',
                letterSpacing: 1,
              }}
            >
              — John Asfar, on the 2008 Global Expansion Strategy
            </Typography>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Timeline;